import { isDefined } from '@/common/utils';
import { BilbyHeader } from '@/components/bilby-header';
import { CompactButton } from '@/components/buttons';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { MarkdownComponent } from '@/components/markdown';
import { ContentCard } from '@/layouts/card';
import { PrimaryLayout } from '@/layouts/primary';
import { TopNavFor } from '@/layouts/primary/top-nav';
import { Avatar, Table, Text, useMantineTheme } from '@mantine/core';
import { IconHelpCircle, IconUserBolt } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { usePastWinnersQuery } from './graphql.generated';

export const PastWinnersPage = () => {
	const { data } = usePastWinnersQuery();
	const { colors } = useMantineTheme();
	const navigate = useNavigate();
	const goToHelp = useCallback(() => navigate({ to: '/help' }), [navigate]);

	const helpButton = useMemo(
		() => (
			<CompactButton leftSection={<IconHelpCircle />} onClick={goToHelp}>
				Help
			</CompactButton>
		),
		[goToHelp]
	);

	const otherWinners = useMemo(() => {
		return data?.pastWinners
			?.filter(isDefined)
			.sort((a, b) => parseInt(b.id) - parseInt(a.id))
			.slice(1);
	}, [data?.pastWinners]);

	const previousWinner = useMemo(() => {
		return data?.pastWinners
			?.filter(isDefined)
			.sort((a, b) => parseInt(b.id) - parseInt(a.id))?.[0];
	}, [data?.pastWinners]);

	return (
		<PrimaryLayout
			context="home"
			header={{ title: 'Entries & Rewards', children: helpButton }}
			topNav={TopNavFor.ENTRIES_AND_REWARDS}
		>
			<ContentPanel hasTopNav gap={14}>
				<BilbyHeader title="Past Winners" bilby="win" />
				{previousWinner && (
					<ContentCard title={previousWinner.heading || 'Previous Winner'}>
						<MarkdownComponent content={previousWinner.content || ''} />
					</ContentCard>
				)}
				<CustomContentSection
					location={ContentSectionLocation.PastWinners}
					placement={ContentSectionPlacement.Top}
				/>
				<ContentCard title="Past Winners">
					<Table.ScrollContainer minWidth={300}>
						<Table verticalSpacing="md">
							<Table.Tbody>
								{otherWinners?.map((item) => (
									<Table.Tr key={item.id}>
										{item.heading ? (
											<Table.Td colSpan={3}>
												<Text size="md" fw={700}>
													{item.heading}
												</Text>
												<MarkdownComponent content={item.content || ''} />
											</Table.Td>
										) : (
											<>
												<Table.Td>
													<Avatar size={50} radius={50} color="gray.0">
														<IconUserBolt color={colors.blue[9]} />
													</Avatar>
												</Table.Td>
												<Table.Td>
													<div>
														<Text fz="sm" fw={700}>
															{item.name}
														</Text>
														<Text c="blue.9" fz="xs">
															{item.postcode}
														</Text>
													</div>
												</Table.Td>
												<Table.Td>
													<div>
														<Text fz="sm">Won</Text>
														<Text c="blue.9" fz="xs" fw={700}>
															{item.prize}
														</Text>
													</div>
												</Table.Td>
											</>
										)}
									</Table.Tr>
								))}
							</Table.Tbody>
						</Table>
					</Table.ScrollContainer>
				</ContentCard>
				<CustomContentSection
					location={ContentSectionLocation.PastWinners}
					placement={ContentSectionPlacement.Middle}
				/>
				<CustomContentSection
					location={ContentSectionLocation.PastWinners}
					placement={ContentSectionPlacement.Bottom}
				/>
			</ContentPanel>
		</PrimaryLayout>
	);
};
